// Course reviews + rating summaries.
import { supabase } from '../integrations/supabase/client';

export interface Review {
  id: string;
  user_id: string;
  course_id: string;
  rating: number;
  title: string;
  body: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  author_name?: string;
}

export async function listCourseReviews(courseId: string): Promise<Review[]> {
  try {
    const { data, error } = await supabase.from('reviews').select('*').eq('course_id', courseId).order('created_at', { ascending: false }).limit(100);
    if (error) return [];
    const rows = (data ?? []) as unknown as Review[];
    const ids = [...new Set(rows.map((r) => r.user_id))];
    if (ids.length === 0) return rows;
    const { data: people } = await supabase.from('profiles_public').select('id,full_name').in('id', ids);
    const names: Record<string, string> = {};
    (people ?? []).forEach((p) => {
      const row = p as unknown as { id: string; full_name: string };
      names[row.id] = row.full_name;
    });
    return rows.map((r) => ({ ...r, author_name: names[r.user_id] || 'Verified learner' }));
  } catch {
    return [];
  }
}

export function ratingSummary(reviews: Review[]): { average: number; count: number; distribution: number[] } {
  const distribution = [0, 0, 0, 0, 0];
  reviews.forEach((r) => {
    const i = Math.min(Math.max(Math.round(r.rating) - 1, 0), 4);
    distribution[i] += 1;
  });
  const count = reviews.length;
  const average = count === 0 ? 0 : reviews.reduce((s, r) => s + r.rating, 0) / count;
  return { average, count, distribution };
}

export async function getMyReview(userId: string, courseId: string): Promise<Review | null> {
  try {
    const { data } = await supabase.from('reviews').select('*').eq('user_id', userId).eq('course_id', courseId).maybeSingle();
    return (data as unknown as Review) ?? null;
  } catch {
    return null;
  }
}

export async function saveReview(args: { userId: string; courseId: string; rating: number; title: string; body: string }): Promise<void> {
  const { error } = await supabase.from('reviews').upsert(
    {
      user_id: args.userId,
      course_id: args.courseId,
      rating: args.rating,
      title: args.title.trim(),
      body: args.body.trim(),
      is_published: true,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,course_id' }
  );
  if (error) throw new Error(error.message);
}

// ---- admin ----
export async function listReviewsAdmin(): Promise<Review[]> {
  const { data, error } = await supabase.from('reviews').select('*').order('created_at', { ascending: false }).limit(300);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Review[];
}

export async function setReviewPublished(id: string, isPublished: boolean): Promise<void> {
  const { error } = await supabase.from('reviews').update({ is_published: isPublished }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteReviewAdmin(id: string): Promise<void> {
  const { error } = await supabase.from('reviews').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
